<template>
  <div class="left-nav">
    <BNav
      id="sidebar-no-header"
      aria-labelledby="sidebar-no-header-title"
      width="243px"
      :no-close-on-route-change="true"
      no-header
      visible
      class="sidebar-nav"
    >
      <div class="d-flex flex-column h-100">
        <div class="nav-content">
          <p class="blade">
            ASUS Annotation Tool
          </p>
          <BNav vertical>
            <LeftNavItem
              :page="{
                name: 'Task Overview',
                path: $const.ROUTES.DASHBOARD,
                imgActive: dashboardHover,
                imgInactive: dashboardNormal,
              }"
            />
            <LeftNavItem
              v-if="userRole === 'admin'"
              :page="{
                name: 'User Management',
                path: $const.ROUTES.USER_MANAGEMENT,
                imgActive: userManagementHover,
                imgInactive: userManagementNormal,
              }"
            />
            <LeftNavItem
              v-if="userRole === 'admin'"
              :page="{
                name: 'Audit Trail',
                path: $const.ROUTES.AUDIT_TRAIL,
                imgActive: auditTrailHover,
                imgInactive: auditTrailNormal,
              }"
            />
            <LeftNavItem
              v-if="dashboardPath"
              :page="{
                name: 'Labelling Progress Tracker',
                path: dashboardPath,
                imgActive: progressDashboardHover,
                imgInactive: progressDashboardNormal,
              }"
            />
          </BNav>
        </div>
        <div class="nav-bottom mt-auto">
          <div class="nav-content mb-3">
            Logged in as<span
              v-if="userName"
              class="user-name"
              :title="userName"
            >{{ userName }}</span>
            <div class="project-info">
              Project: <span
                v-if="projectDisplay"
                class="user-name"
                :title="projectDisplay"
              >{{ projectDisplay }}</span>
            </div>
          </div>
          <div class="nav-content mt-0">
            <BNav vertical>
              <LeftNavItem
                :page="{
                  name: 'Sign out',
                  imgActive: signOutIcon,
                  imgInactive: signOutIcon,
                }"
                :should-emit-click="true"
                @click="logout"
              />
            </BNav>
          </div>
        </div>
      </div>
    </BNav>
  </div>
</template>

<script setup lang="ts">
import LeftNavItem from './LeftNavItem.vue';
import { computed } from 'vue';
import { useConfigStore } from '@/store/config';
import { BNav } from 'bootstrap-vue-next';
import { useApi, useConstants, useCookie } from '@/composables/useAppGlobals';

// static asset imports
import dashboardHover from '@/assets/icons/dashboard_hover.svg';
import dashboardNormal from '@/assets/icons/dashboard_normal.svg';
import userManagementHover from '@/assets/icons/user_management_hover.svg';
import userManagementNormal from '@/assets/icons/user_management_normal.svg';
import progressDashboardHover from '@/assets/icons/progress_dashboard_hover.svg';
import progressDashboardNormal from '@/assets/icons/progress_dashboard_normal.svg';
import auditTrailHover from '@/assets/icons/report_hover.svg';
import auditTrailNormal from '@/assets/icons/report_normal.svg';
import signOutIcon from '@/assets/icons/sign_out.svg';

const store = useConfigStore();
const $api = useApi();
const $const = useConstants();
const $cookie = useCookie();

const userName = computed(() => {
  return $cookie.getCookie($const.COOKIES.KEY.USER_NAME) || '';
});

const userRole = computed(() => {
  return $cookie.getCookie($const.COOKIES.KEY.USER_ROLE) || 'user';
});

const project = computed(() => $api.utils.getProject() || '');

const projectDisplay = computed(() => project.value || '');

const dashboardPath = computed(() => {
  const projectSlug = project.value;
  const kind = ($const.PROJECT_DASHBOARD as Record<string, string>)[projectSlug];
  if (!kind) return '';

  const projectId = $api.utils.getProjectId() || projectSlug;
  if (!projectId) return '';
  const route = kind === 'lpc' ? $const.ROUTES.LPC_DASHBOARD : $const.ROUTES.RBC_DASHBOARD;
  return route.replace(':id', projectId);
});

function logout() {
  store.toggleLogout();
}
</script>

<style lang="scss" scoped>
@use '@/assets/scss/variables' as *;

.left-nav {
  width: $side-bar-width;
  flex-shrink: 0;
}

.nav-content {
  margin-left: 18px;
  margin-top: 30px;
  margin-bottom: 56px;
}

.nav-link {
  padding: 0.7rem 0rem
}

.nav-bottom {
  border-top: 1px solid #E8E8E8;
}

.blade {
  margin-left: 0rem;
  margin-bottom: 1rem;
  margin-top: 24px;
  letter-spacing: 2px;
  color: var(--color-asus-gray-70, #666);
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
}

.user-name {
  display: inline-block;
  max-width: 140px;
  vertical-align: top;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-left: 3px;
}

.project-info {
  margin-top: 8px;
  line-height: 1.5rem;
}

.sidebar-nav {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  background-color: white;
  position: fixed;
  top: 0;
  left: 0;
}
</style>
